import React from 'react';
import { useIntl, type IntlShape } from 'react-intl';
import Dialog from '@components/2-molecules/Dialog';
import type { SkillInUse, SkillUsage, SkillUsageKind } from '@api/agentSkills';
import { Message, UsageKind, UsageList, UsageName, UsageRow } from './SkillInUseDialog.styled';

export type SkillInUseAction = 'delete' | 'unshare';

interface SkillInUseDialogProps {
  readonly skillName: string;
  readonly action: SkillInUseAction;
  readonly inUse: SkillInUse;
  readonly loading: boolean;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}

/** Above the skill form, which stays open behind it during an un-share. */
const ABOVE_FORM_DIALOG = 1001;

/** Names are not unique: two members may each own an app called "Quote checker". */
const withKeys = (usages: readonly SkillUsage[]) => {
  const seen = new Map<string, number>();
  return usages.map((usage) => {
    const base = `${usage.kind}:${usage.name}`;
    const occurrence = seen.get(base) ?? 0;
    seen.set(base, occurrence + 1);
    return { key: `${base}:${occurrence}`, usage };
  });
};

const kindLabel = (kind: SkillUsageKind, intl: IntlShape): string => {
  switch (kind) {
    case 'APP':
      return intl.formatMessage({ id: 'skills.inUse.kind.app', defaultMessage: 'App' });
    case 'PROCESS':
      return intl.formatMessage({ id: 'skills.inUse.kind.process', defaultMessage: 'Process' });
    default: {
      const unhandled: never = kind;
      throw new Error(`Unhandled skill usage kind: ${String(unhandled)}`);
    }
  }
};

const actionText = (action: SkillInUseAction, skillName: string, intl: IntlShape) => {
  switch (action) {
    case 'delete':
      return {
        message: intl.formatMessage(
          {
            id: 'skills.inUse.delete.message',
            defaultMessage: 'Deleting "{name}" will stop these apps and processes from running until they pick another skill.',
          },
          { name: skillName },
        ),
        confirm: intl.formatMessage({ id: 'skills.inUse.delete.confirm', defaultMessage: 'Delete anyway' }),
      };
    case 'unshare':
      return {
        message: intl.formatMessage(
          {
            id: 'skills.inUse.unshare.message',
            defaultMessage:
              'Making "{name}" personal will stop these apps and processes from running until they pick another skill.',
          },
          { name: skillName },
        ),
        confirm: intl.formatMessage({ id: 'skills.inUse.unshare.confirm', defaultMessage: 'Make personal anyway' }),
      };
    default: {
      const unhandled: never = action;
      throw new Error(`Unhandled skill change: ${String(unhandled)}`);
    }
  }
};

const SkillInUseDialog: React.FC<SkillInUseDialogProps> = ({
  skillName,
  action,
  inUse,
  loading,
  onConfirm,
  onCancel,
}) => {
  const intl = useIntl();
  const { message, confirm } = actionText(action, skillName, intl);

  return (
    <Dialog
      show
      dataTestId="skill-in-use-dialog"
      zIndex={ABOVE_FORM_DIALOG}
      headerTitle={intl.formatMessage({ id: 'skills.inUse.title', defaultMessage: 'Skill in use' })}
      onClose={onCancel}
      onCloseIconButtonClick={onCancel}
      onPrimaryButtonClick={onConfirm}
      primaryButtonLabel={confirm}
      primaryButtonLoading={loading}
      onSecondaryButtonClick={onCancel}
      secondaryButtonLabel={intl.formatMessage({ id: 'skills.inUse.cancel', defaultMessage: 'Cancel' })}
      closeOnEscape
      closeOnClickOutside
    >
      <Message>{message}</Message>
      <UsageList data-testid="skill-in-use-dialog__usages">
        {withKeys(inUse.usages).map(({ key, usage }) => (
          <UsageRow key={key}>
            <UsageKind>{kindLabel(usage.kind, intl)}</UsageKind>
            <UsageName>{usage.name}</UsageName>
          </UsageRow>
        ))}
        {inUse.hiddenCount > 0 && (
          <UsageRow>
            <UsageName>
              {intl.formatMessage(
                {
                  id: 'skills.inUse.hidden',
                  defaultMessage:
                    '{count, plural, one {# more app or process} other {# more apps or processes}} owned by other members',
                },
                { count: inUse.hiddenCount },
              )}
            </UsageName>
          </UsageRow>
        )}
      </UsageList>
    </Dialog>
  );
};

export default SkillInUseDialog;
